// ============================================
// moldClaw License Hook
// ============================================

/**
 * 🧑‍💻 SWE-3: 라이선스 상태 관리
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './useAuth';
import {
  License,
  Device,
  getUserLicenses,
  getDevices,
  removeDevice as apiRemoveDevice,
  createPolarCheckout,
  createPolarPortalSession,
  ApiException,
} from '../lib/api';

interface LicenseContextType {
  // 라이선스 데이터
  licenses: License[];
  currentLicense: License | null;
  devices: Device[];
  
  // 로딩 상태
  isLoading: boolean;
  isDevicesLoading: boolean;
  
  // 에러
  error: string | null;
  
  // 액션
  refresh: () => Promise<void>;
  loadDevices: () => Promise<void>;
  removeDevice: (deviceId: string) => Promise<void>;
  checkout: (tier: 'starter' | 'pro' | 'team') => Promise<void>;
  manageSubscription: () => Promise<void>;
  
  // 유틸리티
  hasActiveLicense: boolean;
  canAddDevice: boolean;
  tierLabel: string;
}

const LicenseContext = createContext<LicenseContextType | undefined>(undefined);

export function LicenseProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  
  const [licenses, setLicenses] = useState<License[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDevicesLoading, setIsDevicesLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // 현재 활성 라이선스 (가장 높은 티어)
  const currentLicense = licenses.length > 0
    ? licenses.reduce((best, lic) => {
        if (!best) return lic;
        const tierRank = { free: 0, pro: 1, team: 2 };
        return tierRank[lic.tier] > tierRank[best.tier] ? lic : best;
      }, licenses[0])
    : null;
  
  // 라이선스 데이터 새로고침
  const refresh = useCallback(async () => {
    if (!user) {
      setLicenses([]);
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await getUserLicenses();
      setLicenses(data);
    } catch (err) {
      if (err instanceof ApiException) {
        setError(err.message);
      } else {
        setError('라이선스 정보를 불러오는데 실패했습니다');
      }
      setLicenses([]);
    } finally {
      setIsLoading(false);
    }
  }, [user]);
  
  // 기기 목록 로드
  const loadDevices = useCallback(async () => {
    if (!currentLicense) {
      setDevices([]);
      return;
    }
    
    setIsDevicesLoading(true);
    
    try {
      const data = await getDevices();
      setDevices(data);
    } catch (err) {
      console.error('Failed to load devices:', err);
      setDevices([]);
    } finally {
      setIsDevicesLoading(false);
    }
  }, [currentLicense]);
  
  // 기기 제거
  const removeDevice = useCallback(async (deviceId: string) => {
    try {
      await apiRemoveDevice(deviceId);
      setDevices(prev => prev.filter(d => d.id !== deviceId));
    } catch (err) {
      if (err instanceof ApiException) {
        throw new Error(err.message);
      }
      throw new Error('기기 제거에 실패했습니다');
    }
  }, []);
  
  // Polar Checkout으로 이동
  const checkout = useCallback(async (tier: 'starter' | 'pro' | 'team') => {
    try {
      const { checkout_url } = await createPolarCheckout(tier);
      window.location.href = checkout_url;
    } catch (err) {
      if (err instanceof ApiException) {
        throw new Error(err.message);
      }
      throw new Error('결제 페이지 생성에 실패했습니다');
    }
  }, []);
  
  // Polar Customer Portal로 이동
  const manageSubscription = useCallback(async () => {
    try {
      const { url } = await createPolarPortalSession();
      window.location.href = url;
    } catch (err) {
      if (err instanceof ApiException) {
        throw new Error(err.message);
      }
      throw new Error('구독 관리 페이지 접근에 실패했습니다');
    }
  }, []);
  
  // 사용자 변경 시 라이선스 로드
  useEffect(() => {
    refresh();
  }, [user?.id, refresh]);
  
  // 계산된 값들
  const hasActiveLicense = currentLicense?.status === 'active';
  const canAddDevice = currentLicense 
    ? currentLicense.devices_used < currentLicense.max_devices 
    : false;
  
  const tierLabel = currentLicense 
    ? { free: 'Free', pro: 'Pro', team: 'Team' }[currentLicense.tier]
    : 'None';
  
  return (
    <LicenseContext.Provider
      value={{
        licenses,
        currentLicense,
        devices,
        isLoading,
        isDevicesLoading,
        error,
        refresh,
        loadDevices,
        removeDevice,
        checkout,
        manageSubscription,
        hasActiveLicense,
        canAddDevice,
        tierLabel,
      }}
    >
      {children}
    </LicenseContext.Provider>
  );
}

export function useLicense() {
  const context = useContext(LicenseContext);
  if (context === undefined) {
    throw new Error('useLicense must be used within a LicenseProvider');
  }
  return context;
}

// ============================================
// Tier 정보 상수 (가격 페이지용)
// 💰 2026-03-17 업데이트: 달러 기반, 토큰 중심 가격 정책
// ============================================

export const TIER_INFO = {
  free: {
    name: 'Free',
    tier: 'free',
    price: 0,
    priceLabel: '$0',
    priceYearly: 0,
    priceYearlyLabel: '$0',
    period: '',
    description: 'AI 어시스턴트 체험하기',
    features: [
      '월 5M 토큰',
      '이미지 생성 10개/월',
      '기기 1대 등록',
      'Gemini 2.5 Flash-Lite',
      '커뮤니티 지원',
    ],
    limitations: [
      '상업적 사용 불가',
      '우선 지원 없음',
    ],
    cta: '무료로 시작',
    popular: false,
  },
  starter: {
    name: 'Starter',
    tier: 'starter',
    price: 14.99,
    priceLabel: '$14.99',
    priceYearly: 149.90,
    priceYearlyLabel: '$149.90',
    yearlySavings: '2개월 무료',
    period: '/월',
    description: '개인 사용자를 위한 플랜',
    features: [
      '월 50M 토큰',
      '이미지 생성 80개/월',
      '기기 2대 등록',
      'Gemini 2.5 Flash-Lite',
      '이메일 지원',
      '상업적 사용 가능',
    ],
    limitations: [],
    cta: '시작하기',
    popular: false,
  },
  pro: {
    name: 'Pro',
    tier: 'pro',
    price: 29.99,
    priceLabel: '$29.99',
    priceYearly: 299.90,
    priceYearlyLabel: '$299.90',
    yearlySavings: '2개월 무료',
    period: '/월',
    description: '파워 유저를 위한 플랜',
    features: [
      '월 100M 토큰',
      '이미지 생성 200개/월',
      '기기 3대 등록',
      'Gemini 2.5 Flash-Lite + Flash',
      '우선 지원',
      '고급 기능 (도구 사용, 자동화)',
      '사용량 대시보드',
    ],
    limitations: [],
    cta: '구독하기',
    popular: true,
  },
  team: {
    name: 'Team',
    tier: 'team',
    price: 69.99,
    priceLabel: '$69.99',
    priceYearly: 699.90,
    priceYearlyLabel: '$699.90',
    yearlySavings: '2개월 무료',
    period: '/월',
    description: '팀 협업을 위한 플랜',
    features: [
      '월 300M 토큰',
      '이미지 생성 500개/월',
      '기기 5대 등록',
      '모든 Pro 기능 포함',
      '팀 관리 기능',
      '전용 지원',
      'API 접근',
      '사용량 분석 리포트',
    ],
    limitations: [],
    cta: '팀 시작',
    popular: false,
  },
} as const;

// Add-on 가격 정보
export const ADDON_INFO = {
  claude_s: {
    name: 'Claude S',
    description: 'Claude Sonnet 토큰 팩',
    tokens: '2M',
    price: 19.99,
    priceLabel: '$19.99',
  },
  claude_m: {
    name: 'Claude M',
    description: 'Claude Sonnet 토큰 팩',
    tokens: '5M',
    price: 39.99,
    priceLabel: '$39.99',
  },
  claude_l: {
    name: 'Claude L',
    description: 'Claude Sonnet 토큰 팩',
    tokens: '15M',
    price: 99.99,
    priceLabel: '$99.99',
  },
  image_100: {
    name: 'Image 100',
    description: '이미지 생성 부스터',
    amount: '100개',
    price: 3.99,
    priceLabel: '$3.99',
  },
  search_100: {
    name: 'Search 100',
    description: 'Grounding (검색) 팩',
    amount: '100회',
    price: 6.99,
    priceLabel: '$6.99',
  },
} as const;
